import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const STATUS_BADGE = {
  APPROVED: "bg-success",
  REJECTED: "bg-danger",
  Approved: "bg-success",
  Rejected: "bg-danger",
};

export default function ApprovalHistory() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const employeeId = localStorage.getItem("employeeId");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [downloading, setDownloading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_MANAGER") && !roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const roles = JSON.parse(localStorage.getItem("roles") || "[]");
      const isAdmin = roles.includes("ROLE_ADMIN");
      const isUser = roles.includes("ROLE_USER");
      const isManager = roles.includes("ROLE_MANAGER");

      let url = "";

      if (isAdmin || isUser) {
        // User also gets admin API access
        url = `${API_BASE_URL}/api/asset-requests?page=0&size=100`;
      } else if (isManager) {
        url = `${API_BASE_URL}/api/asset-requests/manager/${employeeId}`;
      }
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.ok ? await res.json() : {};
      const all = Array.isArray(data) ? data : data.content || [];
      setHistory(all.filter(r => r.status !== "Pending" && r.status !== "PENDING"));
    } catch {
      setHistory([]);
      toast.error("Unable to load approval history.");
    } finally { setLoading(false); }
  }, [token, employeeId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      const res = await fetch(`${API_BASE_URL}/api/asset-requests/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to delete request");
      toast.success("Request deleted successfully");
      fetchHistory();
    } catch (err) {
      toast.error(err.message || "Unable to delete request");
    } finally {
      setDeletingId(null);
      setDeleteModal({ open: false, id: null });
    }
  };

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      const res = await fetch(`${API_BASE_URL}/api/asset-requests/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download report");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      const hasFilters = search || statusFilter !== "ALL";
      a.href = url;
      a.download = hasFilters ? `Approval_History_Filtered_${date}.xlsx` : `Approval_History_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return history.filter(r => {
      const matchStatus = statusFilter === "ALL" || (r.status || "").toLowerCase() === statusFilter.toLowerCase();
      if (!q) return matchStatus;
      return matchStatus && (
        (r.assetCategory || "").toLowerCase().includes(q) ||
        (r.employeeName || "").toLowerCase().includes(q) ||
        (r.status || "").toLowerCase().includes(q)
      );
    });
  }, [history, search, statusFilter]);

  const approved = history.filter(r => r.status === "Approved" || r.status === "APPROVED").length;
  const rejected = history.filter(r => r.status === "Rejected" || r.status === "REJECTED").length;

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-clock-history me-2"></i> Approval History</h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>Complete record of all approved and rejected asset allocation requests.</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-warning btn-sm" onClick={() => navigate("/pendingapprovals")}>
              <i className="bi bi-hourglass-split me-1"></i> Pending Requests
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={downloadReport} disabled={downloading}>
              <i className="bi bi-download me-1"></i>{downloading ? "Downloading..." : "Export Excel"}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Back</button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Total Processed", value: history.length, icon: "bi-clock-history", color: "#1e3a5f" },
            { label: "Approved", value: approved, icon: "bi-check-circle", color: "#198754" },
            { label: "Rejected", value: rejected, icon: "bi-x-circle", color: "#dc3545" },
          ].map(item => (
            <div className="col-md-4" key={item.label}>
              <div className="bg-white border rounded p-3 d-flex align-items-center gap-3">
                <i className={`bi ${item.icon}`} style={{ fontSize: 24, color: item.color }}></i>
                <div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{item.label}</div>
                  <div className="fw-bold" style={{ fontSize: 22, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters(p => !p)}>
          <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
        </div>
        {showFilters && (
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input type="text" className="form-control form-control-sm"
                placeholder="Search by asset, employee..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="ALL">All Statuses</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(""); setStatusFilter("ALL"); }}>Clear</button>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
          <span className="text-muted" style={{ fontSize: 13 }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading approval history...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-clock-history" style={{ fontSize: 32, opacity: 0.4 }}></i>
            <div className="mt-2">{search ? "No records match your search." : "No approval history found."}</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm align-middle text-start">
              <thead className="table-light">
                <tr>
                  <th>Sr.</th><th>Asset</th><th>Employee</th><th>Priority</th>
                  <th>Requested Date</th><th>Expected By</th><th>Justification</th>
                  <th>Status</th><th>Remarks</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => (
                  <tr key={item.id}>
                    <td style={{ color: "#9ca3af", fontSize: 12 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{item.assetCategory || "—"}</td>
                    <td>{item.employeeName || item.employeeId || "—"}</td>
                    <td>
                      {item.priority && (
                        <span className={`badge ${item.priority === "Critical" ? "bg-danger" : item.priority === "High" ? "bg-warning text-dark" : item.priority === "Medium" ? "bg-info text-dark" : "bg-secondary"}`}>
                          {item.priority}
                        </span>
                      )}
                    </td>
                    <td>{item.requestedDate || item.createdAt || "—"}</td>
                    <td>{item.expectedDate || "—"}</td>
                    <td style={{ maxWidth: 200 }}><small className="text-muted">{item.justification || "—"}</small></td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[item.status] || "bg-secondary"}`}>{item.status}</span>
                    </td>
                    <td style={{ maxWidth: 200 }}><small className="text-muted">{item.adminRemarks || "—"}</small></td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setDeleteModal({ open: true, id: item.id })}
                        disabled={deletingId === item.id}
                        title="Delete"
                      >
                        {deletingId === item.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-trash"></i>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Delete Confirmation Modal ── */}
      {deleteModal.open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setDeleteModal({ open: false, id: null })}>
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", width: "100%", maxWidth: 400, padding: "28px 28px 22px" }}
            onClick={e => e.stopPropagation()}>
            <div className="d-flex align-items-center gap-3 mb-3">
              <div style={{ width: 42, height: 42, borderRadius: 10, background: "#fff0f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-trash3" style={{ fontSize: 20, color: "#dc3545" }} />
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: 16 }}>Delete Request</div>
                <div className="text-muted mt-1" style={{ fontSize: 13, lineHeight: 1.5 }}>Are you sure you want to delete this request? This action cannot be undone.</div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button className="btn btn-outline-secondary btn-sm px-4" onClick={() => setDeleteModal({ open: false, id: null })} disabled={deletingId === deleteModal.id}>Cancel</button>
              <button className="btn btn-danger btn-sm px-4" onClick={() => handleDelete(deleteModal.id)} disabled={deletingId === deleteModal.id}>
                {deletingId === deleteModal.id ? <><span className="spinner-border spinner-border-sm me-1" />Deleting…</> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
